type BranchNode = { level: number; data: { name: string } };
type BranchLabel = { label: string; description?: string };

const formatSkillBranchLabel = (skill: string, granularity: string) => {
  return `${skill.split('/')[0]?.trim() ?? skill} ... ${granularity}`;
};

export const ricsAndCompanyBranchLabels = (
  parentNode: BranchNode
): BranchLabel => {
  const { level, data } = parentNode;
  const label = (() => {
    switch (level) {
      case -1:
        return 'Industry k10';
      case 0:
        return `${data.name} k400`;
      case 1:
        return `${data.name} Companies`;
      default:
        return 'Industry';
    }
  })();
  return { label };
};

/**
 * The same convention for a k10 → k50 → k400 industry tree with no company
 * level (the Economic Impact scenario picker): each panel is named by its
 * granularity, prefixed with the node it was drilled from.
 */
export const ricsIndustryBranchLabels = (
  parentNode: BranchNode
): BranchLabel => {
  const { level, data } = parentNode;
  const label = (() => {
    switch (level) {
      case -1:
        return 'Industry k10';
      case 0:
        return `${data.name} k50`;
      case 1:
        return `${data.name} k400`;
      default:
        return 'Industry';
    }
  })();
  return { label };
};

export const roleBranchLabels = (parentNode: BranchNode): BranchLabel => {
  const { level, data } = parentNode;
  const { label, description } = (() => {
    switch (level) {
      case -1:
        return {
          label: 'Job Category',
          description:
            'Job Category is a set of distinct occupations. At this level of granularity, roles are bucketed into 7 parts.',
        };
      case 0:
        return {
          label: `${data.name} k150`,
          description:
            'Role k150 is a set of distinct occupations. At this level of granularity, roles are bucketed into 150 parts.',
        };
      case 1:
        return {
          label: `${data.name} k1500`,
          description:
            'Role k1500 is a set of distinct occupations. At this level of granularity, roles are bucketed into 1,500 parts.',
        };
      case 2:
        return {
          label: `${data.name} k15000`,
          description:
            'Role k15000 is a set of distinct occupations. At this level of granularity, roles are bucketed into 15,000 parts.',
        };
      default:
        return {
          label: 'Role',
          description: 'Role is a set of distinct occupations',
        };
    }
  })();
  return { label, description };
};

/**
 * Only for the two surfaces that are metro-shaped by definition and never
 * follow the geography preference: the company HQ-location filter, and
 * rebuilding a geography filter from a URL before any taxonomy has loaded.
 *
 * The GEOGRAPHY filter itself takes its branch labels from the active
 * taxonomy's `levels[].label` (see `useGeographyTaxonomy`) — a constant can't
 * name a fourth level that differs per taxonomy.
 */
export const metroGeographyBranchLabels: BranchLabel[] = [
  { label: 'Region' },
  { label: 'Country' },
  { label: 'Metro Area' },
  { label: 'City' },
];

export const skillsBranchLabels = (parentNode: BranchNode): BranchLabel => {
  const { level, data } = parentNode;
  const label = (() => {
    switch (level) {
      case -1:
        return 'Skill k75';
      case 0:
        return formatSkillBranchLabel(data.name, 'k700');
      case 1:
        return formatSkillBranchLabel(data.name, 'k3000');
      default:
        return 'Skills';
    }
  })();
  return { label };
};
